import React from 'react';
import styled from '@emotion/styled';
import { ProductListItem } from '../../types/types';
import { Config, StyleLibrary } from '@vm/omega-shared-component-webapp';
import { RadioInputWithLabel } from '../shared/Form/RadioInputWithLabel';

const { pxToRem, mqBreakpoints } = StyleLibrary;

const Li = styled.li<{ isSelected: boolean }>(props => ({
  background: 'var(--color-white)',
  listStyleType: 'none',
  width: '100%',
  cursor: 'pointer',
  border: '2px solid',
  borderColor: props.isSelected ? 'var(--color-lime-darker)' : 'var(--color-mid-grey)',

  '&:hover, &:focus': {
    boxShadow: '0 0 0 1px var(--color-mid-grey)',
  },

  [mqBreakpoints.tablet]: {
    background: 'transparent',
    border: 'none',
    '&:hover, &:focus': {
      boxShadow: 'none',
    },
  },
}));

const ItemContainer = styled.div({
  width: 'inherit',
  height: pxToRem(50),
  position: 'relative',
  marginBottom: pxToRem(17),

  [mqBreakpoints.tablet]: {
    height: pxToRem(125),
  },
});

const ImgContainer = styled.div({
  width: '75%',
  height: 'inherit',
  [mqBreakpoints.tablet]: {
    width: 'inherit',
  },
});

const Img = styled.img<{ isSelected: boolean }>(props => ({
  padding: 0,
  margin: `${pxToRem(24)} ${pxToRem(16)}`,
  background: 'var(--color-white)',
  width: 'inherit',
  height: 'inherit',
  objectFit: 'contain',

  [mqBreakpoints.tablet]: {
    padding: `${pxToRem(16)} ${pxToRem(24)}`,
    margin: 0,
    border: '2px solid',
    borderColor: props.isSelected ? 'var(--color-lime-darker)' : 'var(--color-mid-grey)',

    '&:hover, &:focus': {
      boxShadow: '0 0 0 1px var(--color-mid-grey)',
    },
  },
}));

const OfferBadge = styled.span({
  position: 'absolute',
  top: '1px',
  right: '1px',
  padding: pxToRem(8),
  backgroundColor: 'var(--color-lime)',
  color: 'var(--color-black)',
  fontWeight: 'var(--font-weight-medium)',
  fontSize: pxToRem(12),

  [mqBreakpoints.desktop]: {
    fontSize: pxToRem(16),
    top: pxToRem('-17'),
    right: pxToRem('-24'),
  },
});

type Props = {
  tile: ProductListItem;
  index: number;
  isSelected: boolean;
  onClick: (i: number) => void;
};

export const TileTp: React.FC<Props> = ({ tile, index, isSelected, onClick }) => {
  const metadata = tile?.product?.metadata;
  const productId = tile?.product?.productId;
  const img = metadata.image;
  const logo = metadata?.logo;
  const name = metadata?.title; // these are swapped around in Java: title <-> name
  const offerBadge = metadata?.tile?.offer?.offerBadge;

  const id = `product-${index}`;
  const formattedName = name ? name.replace(/\s/g, '-').toLowerCase() : '';
  const altName = `${name} logo`;

  return (
    <Li isSelected={isSelected} onClick={() => onClick(index)} data-testid={`mp-tile-tp-${productId}`}>
      <ItemContainer>
        <ImgContainer data-testid='mp-tile-tp-img'>
          {logo && <Img src={`${Config.get('image_cdn_base_url')}/${logo}`} isSelected={isSelected} alt={altName} />}
          {!logo && img && <Img src={`${Config.get('image_cdn_base_url')}/${img}`} isSelected={isSelected} alt={altName} />}
        </ImgContainer>
        {offerBadge && (
          <OfferBadge aria-hidden='true' data-testid='mp-tile-offerbadge'>
            {offerBadge}
          </OfferBadge>
        )}
      </ItemContainer>

      <RadioInputWithLabel id={`radio-${id}-${index}-${formattedName}`} data-testid={`radio-${id}-${formattedName}`} checked={isSelected} value={formattedName} label={name} onChange={() => index} />
    </Li>
  );
};
